import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  FileText, 
  CheckCircle, 
  AlertTriangle, 
  TrendingUp,
  Plus,
  ArrowRight,
  AlertCircle,
  Coins
} from 'lucide-react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend
} from 'recharts';

import { API_URL } from '../config';

const MONTHS = [
  'Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu',
  'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic'
];

export default function DashboardPage() {
  const [stats, setStats] = useState(null);
  const [credits, setCredits] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      const [dashResponse, creditsResponse] = await Promise.all([
        axios.get(`${API_URL}/dashboard`),
        axios.get(`${API_URL}/credits`)
      ]);
      setStats(dashResponse.data);
      setCredits(creditsResponse.data);
    } catch (err) {
      setError('Errore nel caricamento della dashboard');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR'
    }).format(value);
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="spinner" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-8" data-testid="dashboard">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold tracking-tight">Dashboard</h1>
            <p className="text-muted-foreground mt-1">Panoramica delle tue buste paga</p>
          </div>
          <Button asChild className="swiss-btn-primary">
            <Link to="/upload" data-testid="new-payslip-btn">
              <Plus className="w-4 h-4 mr-2" />
              Nuova Busta Paga
            </Link>
          </Button>
        </div>

        {error && (
          <div className="flex items-center gap-2 p-4 bg-red-50 border border-red-200 text-red-700 text-sm rounded-sm">
            <AlertCircle className="w-4 h-4" />
            {error}
          </div>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="swiss-card">
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="label-uppercase mb-2">Buste Totali</p>
                  <p className="stat-highlight">{stats?.total_payslips || 0}</p>
                </div>
                <div className="w-10 h-10 bg-mint rounded-sm flex items-center justify-center">
                  <FileText className="w-5 h-5 text-forest" strokeWidth={1.5} />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="swiss-card">
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="label-uppercase mb-2">Verificate OK</p>
                  <p className="stat-highlight text-emerald-600">{stats?.valid_payslips || 0}</p>
                </div>
                <div className="w-10 h-10 bg-emerald-100 rounded-sm flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-emerald-600" strokeWidth={1.5} />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="swiss-card">
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="label-uppercase mb-2">Con Anomalie</p>
                  <p className="stat-highlight text-amber-600">{stats?.invalid_payslips || 0}</p>
                </div>
                <div className="w-10 h-10 bg-amber-100 rounded-sm flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-amber-600" strokeWidth={1.5} />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="swiss-card">
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="label-uppercase mb-2">Netto Medio</p>
                  <p className="stat-highlight text-lg md:text-xl">{formatCurrency(stats?.avg_net_salary || 0)}</p>
                </div>
                <div className="w-10 h-10 bg-blue-100 rounded-sm flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-blue-600" strokeWidth={1.5} />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Credits Card */}
        <Card className="swiss-card bg-gradient-to-r from-forest to-emerald-700 text-white">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                  <Coins className="w-6 h-6" />
                </div>
                <div>
                  <p className="text-white/70 text-sm">I tuoi Crediti</p>
                  <p className="text-3xl font-bold font-mono">{credits?.credits ?? '-'}</p>
                </div>
              </div>
              <Button asChild variant="secondary" className="bg-white text-forest hover:bg-white/90">
                <Link to="/credits">
                  {credits?.credits === 0 ? 'Acquista' : 'Gestisci'}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Link>
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Chart and Recent */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Chart */}
          <Card className="swiss-card lg:col-span-2">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg font-semibold">Andamento Retribuzione</CardTitle>
            </CardHeader>
            <CardContent>
              {stats?.monthly_trend && stats.monthly_trend.length > 0 ? (
                <div className="chart-container">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={stats.monthly_trend}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis 
                        dataKey="period" 
                        tick={{ fontSize: 12 }}
                        stroke="#94a3b8"
                      />
                      <YAxis 
                        tick={{ fontSize: 12 }}
                        stroke="#94a3b8"
                        tickFormatter={(v) => `${(v/1000).toFixed(0)}k`}
                      />
                      <Tooltip 
                        formatter={(value) => formatCurrency(value)}
                        labelStyle={{ color: '#064e3b' }}
                        contentStyle={{ 
                          borderRadius: '2px',
                          border: '1px solid #e2e8f0'
                        }}
                      />
                      <Legend />
                      <Line 
                        type="monotone" 
                        dataKey="gross" 
                        name="Lordo"
                        stroke="#064e3b" 
                        strokeWidth={2}
                        dot={{ fill: '#064e3b', r: 4 }}
                      />
                      <Line 
                        type="monotone" 
                        dataKey="net" 
                        name="Netto"
                        stroke="#10b981" 
                        strokeWidth={2}
                        dot={{ fill: '#10b981', r: 4 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="empty-state py-12">
                  <TrendingUp className="w-12 h-12 text-slate-300 mb-4" />
                  <p className="text-muted-foreground">Nessun dato disponibile</p>
                  <p className="text-sm text-muted-foreground">Carica le tue buste paga per vedere l'andamento</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Recent Payslips */}
          <Card className="swiss-card">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg font-semibold">Ultime Buste</CardTitle>
                <Link 
                  to="/history" 
                  className="text-sm text-forest hover:underline flex items-center gap-1"
                  data-testid="view-all-link"
                >
                  Vedi tutte <ArrowRight className="w-3 h-3" />
                </Link>
              </div>
            </CardHeader>
            <CardContent>
              {stats?.recent_payslips && stats.recent_payslips.length > 0 ? (
                <div className="space-y-3">
                  {stats.recent_payslips.map((payslip) => (
                    <Link
                      key={payslip.id}
                      to={`/payslip/${payslip.id}`}
                      className="block p-3 border border-slate-200 hover:border-forest/30 transition-colors rounded-sm"
                      data-testid={`payslip-item-${payslip.id}`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-medium text-sm">
                          {MONTHS[payslip.period_month - 1]} {payslip.period_year}
                        </span>
                        {payslip.is_valid ? (
                          <span className="valid-badge">
                            <CheckCircle className="w-3 h-3" /> OK
                          </span>
                        ) : (
                          <span className="invalid-badge">
                            <AlertTriangle className="w-3 h-3" /> {payslip.total_issues}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">{payslip.company_name}</span>
                        <span className="font-mono text-forest">{formatCurrency(payslip.net_salary)}</span>
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="empty-state py-8">
                  <FileText className="w-10 h-10 text-slate-300 mb-3" />
                  <p className="text-sm text-muted-foreground">Nessuna busta paga</p>
                  <Button asChild className="swiss-btn-primary mt-4" size="sm">
                    <Link to="/upload">
                      <Plus className="w-4 h-4 mr-1" /> Carica ora
                    </Link>
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Issues Summary */}
        {stats?.total_issues > 0 && (
          <Card className="swiss-card border-amber-200 bg-amber-50/50">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-amber-100 rounded-sm flex items-center justify-center flex-shrink-0">
                  <AlertTriangle className="w-5 h-5 text-amber-600" strokeWidth={1.5} />
                </div>
                <div>
                  <h3 className="font-semibold text-amber-800 mb-1">
                    {stats.total_issues} anomalie rilevate
                  </h3>
                  <p className="text-sm text-amber-700">
                    Sono state trovate {stats.total_issues} possibili anomalie nelle tue buste paga. 
                    Verifica i dettagli nella sezione storico.
                  </p>
                  <Button asChild variant="ghost" className="mt-2 text-amber-700 hover:text-amber-800 hover:bg-amber-100 p-0 h-auto">
                    <Link to="/history">
                      Verifica anomalie <ArrowRight className="w-4 h-4 ml-1" />
                    </Link>
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
}
